<template>
    <modal id="beneficiary-badge-create-edit-modal"
           size="large"
           v-model="showModal"
           :title="this.$t('payslip')"
           :cancel-btn-label="$t('close')"
           :hide-submit-button="true"
           @submit=""
           :preloader="preloader">

        <div class="ps-sheet">
            <!-- Header -->
            <div class="ps-header d-flex justify-content-between align-items-start">
                <div>
                    <span class="ps-logo-chip">
                        <img :src="logo" alt="logo"/>
                    </span>
                    <h5 class="ps-co-name mb-0">{{ payrunData.title ? payrunData.title : generalSettings.tenant_name }}</h5>
                    <p class="ps-co-addr mb-0" v-if="payrunData.address" style="white-space: pre-line;">{{ payrunData.address }}</p>
                    <p class="ps-co-addr mb-0" v-else>
                        {{ generalSettings.address ? `${generalSettings.address}, ` : '' }}{{ generalSettings.area ? `${generalSettings.area}, ` : '' }}{{ generalSettings.city ? generalSettings.city : '' }}
                        <template v-if="generalSettings.country"><br/>{{ generalSettings.zip_code ? `ZIP ${generalSettings.zip_code}, ` : '' }}{{ generalSettings.country }}</template>
                    </p>
                </div>
                <div class="text-right">
                    <div class="ps-doc-title">SALARY SLIP</div>
                    <div class="ps-doc-meta">{{ getDateDifferenceString(payslip.start_date, payslip.end_date) }}</div>
                    <div class="ps-doc-meta">{{ this.$t('created_at') }}: {{ customDateFormat(payslip.created_at) }}</div>
                </div>
            </div>

            <!-- Net pay strip -->
            <div class="ps-net d-flex justify-content-between align-items-center">
                <div>
                    <div class="ps-net-label">{{ this.$t('net_payable_salary') }}</div>
                    <div class="ps-net-amount">{{ formatCurrency(numberFormatter(Number(payslip.net_salary))) }}</div>
                </div>
                <span class="ps-paid-pill">{{ this.$t('paid').toUpperCase() }}</span>
            </div>

            <!-- Employee details -->
            <div class="ps-section">
                <div class="ps-section-title">{{ this.$t('employee') }}</div>
                <div class="row">
                    <div class="col-6">
                        <div class="ps-kv"><span class="ps-k">{{ this.$t('name') }}</span><span class="ps-v">{{ user.full_name }}</span></div>
                        <div class="ps-kv"><span class="ps-k">{{ this.$t('email') }}</span><span class="ps-v">{{ user.email }}</span></div>
                        <div class="ps-kv" v-if="user.profile && user.profile.employee_id"><span class="ps-k">{{ this.$t('employee_id') }}</span><span class="ps-v">{{ user.profile.employee_id }}</span></div>
                    </div>
                    <div class="col-6">
                        <div class="ps-kv"><span class="ps-k">{{ this.$t('designation') }}</span><span class="ps-v">{{ user.designation ? user.designation.name : '—' }}</span></div>
                        <div class="ps-kv"><span class="ps-k">{{ this.$t('department') }}</span><span class="ps-v">{{ user.department ? user.department.name : '—' }}</span></div>
                        <div class="ps-kv"><span class="ps-k">{{ this.$t('payslip_for') }}</span><span class="ps-v">{{ getDateDifferenceString(payslip.start_date, payslip.end_date) }}</span></div>
                    </div>
                </div>
            </div>

            <!-- Earnings / Deductions -->
            <div class="row ps-ed">
                <div class="col-md-6 mb-3 mb-md-0">
                    <div class="ps-card">
                        <div class="ps-card-title ps-earn">Earnings</div>
                        <div class="ps-line"><span>{{ this.$t('basic_salary') }}</span><span>{{ formatCurrency(numberFormatter(basicSalary)) }}</span></div>
                        <div class="ps-line" v-if="overtime > 0"><span>{{ this.$t('overtime_earning') }}</span><span>{{ formatCurrency(numberFormatter(overtime)) }}</span></div>
                        <div class="ps-line" v-for="(b, i) in allowanceList" :key="`a-${i}`">
                            <span>{{ b.name }}<small class="ps-muted" v-if="b.percent"> ({{ b.percent }}%)</small></span>
                            <span>{{ formatCurrency(numberFormatter(b.value)) }}</span>
                        </div>
                        <div class="ps-line ps-sub"><span>{{ this.$t('total_earning') }}</span><span>{{ formatCurrency(numberFormatter(grossEarnings)) }}</span></div>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="ps-card">
                        <div class="ps-card-title ps-ded">{{ this.$t('deductions') }}</div>
                        <div class="ps-line" v-if="unpaidLeave > 0"><span>{{ this.$t('unpaid_leave_deduction') }}</span><span>{{ formatCurrency(numberFormatter(unpaidLeave)) }}</span></div>
                        <div class="ps-line" v-for="(b, i) in deductionList" :key="`d-${i}`">
                            <span>{{ b.name }}<small class="ps-muted" v-if="b.percent"> ({{ b.percent }}%)</small></span>
                            <span>{{ formatCurrency(numberFormatter(b.value)) }}</span>
                        </div>
                        <div class="ps-line ps-muted" v-if="unpaidLeave === 0 && !deductionList.length"><span>No deductions</span><span>{{ formatCurrency(numberFormatter(0)) }}</span></div>
                        <div class="ps-line ps-sub"><span>{{ this.$t('total_deduction') }}</span><span>{{ formatCurrency(numberFormatter(allDeductions)) }}</span></div>
                    </div>
                </div>
            </div>

            <!-- Totals -->
            <div class="ps-totals">
                <div class="ps-tline"><span class="ps-tl">{{ this.$t('total_earning') }}</span><span class="ps-tv">{{ formatCurrency(numberFormatter(grossEarnings)) }}</span></div>
                <div class="ps-tline"><span class="ps-tl">{{ this.$t('total_deduction') }}</span><span class="ps-tv">&minus; {{ formatCurrency(numberFormatter(allDeductions)) }}</span></div>
                <div class="ps-tline ps-tnet"><span class="ps-tl">{{ this.$t('net_payable_salary') }}</span><span class="ps-tv">{{ formatCurrency(numberFormatter(Number(payslip.net_salary))) }}</span></div>
            </div>
            <p class="ps-words">Amount in words: {{ amountInWords }} only.</p>

            <p v-if="payrunData.note" class="ps-note"><b>{{ this.$t('note') }}:</b> {{ payrunData.note }}</p>
        </div>

    </modal>
</template>

<script>
import ModalMixin from "../../../../../common/Mixin/Global/ModalMixin";
import {urlGenerator} from "../../../../../common/Helper/AxiosHelper";
import {mapState} from "vuex";
import {customDateFormat, getDateDifferenceString} from "../../../../../common/Helper/Support/DateTimeHelper";
import {formatCurrency as currencyFormatter, numberFormatter} from "../../../../../common/Helper/Support/SettingsHelper";

export default {
    name: "PayslipViewModal",
    mixins: [ModalMixin],
    props: {
        payslip: {},
        settings: {}
    },
    data() {
        return {
            urlGenerator,
            customDateFormat,
            getDateDifferenceString,
            numberFormatter
        }
    },
    methods: {
        // Format money in this payslip's (employee's) own currency.
        formatCurrency(amount) {
            return currencyFormatter(amount, this.payslip && this.payslip.currency);
        },
        getPercentageOfSalary(amount) {
            return ((this.basicSalary / 100) * Number(amount));
        },
        numberToWords(num) {
            num = Math.floor(Math.abs(Number(num) || 0));
            if (num === 0) return 'Zero';
            const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
                'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
            const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
            const inWords = (n) => {
                if (n < 20) return a[n];
                if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
                if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + inWords(n % 100) : '');
                if (n < 1000000) return inWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + inWords(n % 1000) : '');
                if (n < 1000000000) return inWords(Math.floor(n / 1000000)) + ' Million' + (n % 1000000 ? ' ' + inWords(n % 1000000) : '');
                return inWords(Math.floor(n / 1000000000)) + ' Billion' + (n % 1000000000 ? ' ' + inWords(n % 1000000000) : '');
            };
            return inWords(num);
        },
    },
    computed: {
        ...mapState({
            generalSettings: state => state.settings
        }),
        logo() {
            if (this.payrunData.logo) {
                return urlGenerator(this.payrunData.logo)
            }
            return this.$store.state.settings.tenant_logo
                ? urlGenerator(this.$store.state.settings.tenant_logo)
                : urlGenerator('/images/logo/default-logo.png')
        },
        payrunData() {
            return JSON.parse(this.payslip.payrun.data);
        },
        user() {
            return this.payslip.user;
        },
        basicSalary() {
            return Number(this.payslip.basic_salary);
        },
        earnedSalary() {
            return Number(this.payslip.net_salary) - this.totalBeneficiary;
        },
        overtime() {
            return this.earnedSalary > this.basicSalary ? this.earnedSalary - this.basicSalary : 0;
        },
        unpaidLeave() {
            return this.earnedSalary < this.basicSalary ? this.basicSalary - this.earnedSalary : 0;
        },
        grossEarnings() {
            return this.basicSalary + this.overtime + this.totalAllowances;
        },
        allDeductions() {
            return this.unpaidLeave + this.totalDeductions;
        },
        amountInWords() {
            return this.numberToWords(Number(this.payslip.net_salary));
        },
        allowanceList() {
            return this.beneficiaries
                .filter(b => b.beneficiary.type === 'allowance')
                .map(b => ({
                    name: b.beneficiary.name,
                    percent: parseInt(b.is_percentage) ? b.amount : null,
                    value: parseInt(b.is_percentage) ? this.getPercentageOfSalary(b.amount) : Number(b.amount),
                }));
        },
        deductionList() {
            return this.beneficiaries
                .filter(b => b.beneficiary.type === 'deduction')
                .map(b => ({
                    name: b.beneficiary.name,
                    percent: parseInt(b.is_percentage) ? b.amount : null,
                    value: parseInt(b.is_percentage) ? this.getPercentageOfSalary(b.amount) : Number(b.amount),
                }));
        },
        beneficiaries() {
            return this.payslip.beneficiaries.length
                ? this.payslip.beneficiaries
                : (parseInt(this.payslip.without_beneficiary) ? [] : this.payslip.payrun.beneficiaries);
        },
        totalBeneficiary() {
            if (!this.beneficiaries.length) {
                return 0;
            }
            return Number(this.totalAllowances - this.totalDeductions);
        },
        totalAllowances() {
            return this.beneficiaries.reduce((allowance, beneficiary) => {
                if (beneficiary.beneficiary.type === 'allowance') {
                    return parseInt(beneficiary.is_percentage)
                        ? ((this.basicSalary / 100) * Number(beneficiary.amount)) + allowance
                        : Number(beneficiary.amount) + allowance;
                } else {
                    return allowance;
                }
            }, 0);
        },
        totalDeductions() {
            return this.beneficiaries.reduce((deduction, beneficiary) => {
                if (beneficiary.beneficiary.type === 'deduction') {
                    return parseInt(beneficiary.is_percentage)
                        ? ((this.basicSalary / 100) * Number(beneficiary.amount)) + deduction
                        : Number(beneficiary.amount) + deduction;
                } else {
                    return deduction;
                }
            }, 0);
        }
    }
}
</script>

<style scoped>
.ps-sheet {
    border: 1px solid var(--default-border-color);
    border-radius: 10px;
    overflow: hidden;
}

.ps-header {
    background: linear-gradient(120deg, #08205c 0%, #1d3a82 100%);
    color: #ffffff;
    padding: 22px 24px;
}

.ps-logo-chip {
    background: #ffffff;
    border-radius: 8px;
    padding: 6px 10px;
    display: inline-block;
    margin-bottom: 9px;
}

.ps-logo-chip img {
    max-height: 42px;
    max-width: 150px;
}

.ps-co-name {
    font-size: 15px;
    font-weight: 600;
}

.ps-co-addr {
    font-size: 11px;
    color: #c2cde6;
    margin-top: 3px;
}

.ps-doc-title {
    font-size: 19px;
    font-weight: 700;
    letter-spacing: 1px;
}

.ps-doc-meta {
    font-size: 11px;
    color: #c2cde6;
    margin-top: 3px;
}

.ps-net {
    background: #eef3fb;
    border-bottom: 1px solid #e0e7f3;
    padding: 15px 24px;
}

.ps-net-label {
    font-size: 10px;
    color: #5b6f95;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.ps-net-amount {
    font-size: 24px;
    font-weight: 700;
    color: #08205c;
}

.ps-paid-pill {
    background: #10b981;
    color: #ffffff;
    font-size: 11px;
    font-weight: 600;
    padding: 5px 16px;
    border-radius: 16px;
}

.ps-section {
    padding: 18px 24px 4px;
}

.ps-section-title {
    font-size: 11px;
    font-weight: 600;
    color: #08205c;
    text-transform: uppercase;
    letter-spacing: 1px;
    border-bottom: 2px solid #2563eb;
    padding-bottom: 6px;
    margin-bottom: 12px;
}

.ps-kv {
    display: flex;
    justify-content: space-between;
    padding: 3px 0;
    font-size: 12.5px;
}

.ps-k {
    color: var(--default-secondary-color, #7b8398);
}

.ps-v {
    font-weight: 600;
    text-align: right;
}

.ps-ed {
    padding: 6px 24px 0;
}

.ps-card {
    border: 1px solid var(--default-border-color);
    border-radius: 8px;
    overflow: hidden;
}

.ps-card-title {
    font-size: 11px;
    font-weight: 600;
    color: #ffffff;
    padding: 8px 14px;
}

.ps-earn {
    background: #2563eb;
}

.ps-ded {
    background: #64748b;
}

.ps-line {
    display: flex;
    justify-content: space-between;
    padding: 8px 14px;
    font-size: 12px;
    border-top: 1px solid var(--default-border-color);
}

.ps-card .ps-line:first-of-type {
    border-top: none;
}

.ps-sub {
    font-weight: 600;
    background: var(--base-color);
    border-top: 2px solid var(--default-border-color);
}

.ps-muted {
    color: var(--default-secondary-color, #8893a5);
}

.ps-totals {
    margin: 18px 24px 6px;
    border-radius: 8px;
    background: linear-gradient(120deg, #08205c 0%, #1d3a82 100%);
    color: #ffffff;
    padding: 10px 22px;
}

.ps-tline {
    display: flex;
    justify-content: space-between;
    padding: 7px 0;
}

.ps-tl {
    font-size: 12.5px;
    color: #c2cde6;
}

.ps-tv {
    font-size: 13px;
    font-weight: 600;
}

.ps-tnet {
    border-top: 1px solid #1d3a72;
    margin-top: 4px;
    padding-top: 12px;
}

.ps-tnet .ps-tl {
    color: #ffffff;
    font-weight: 600;
    font-size: 13px;
}

.ps-tnet .ps-tv {
    font-size: 20px;
    color: #7fd7bd;
}

.ps-words {
    padding: 0 24px;
    font-size: 11.5px;
    color: var(--default-secondary-color, #5b6273);
    font-style: italic;
}

.ps-note {
    margin: 10px 24px 4px;
    padding-top: 10px;
    border-top: 1px solid var(--default-border-color);
    font-size: 11.5px;
    font-style: italic;
}
</style>
